import React, { useState } from 'react';
import { toast } from 'sonner';
import { Gift, CheckCircle2 } from 'lucide-react';
import { useReadyTranslation } from '../hooks/useReadyTranslation';
import { useAuth } from '../contexts/AuthContext';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { Boton, CajaLargaConContador, BloqueEsqueleto, avisarSinRed } from './alumno';
import { CAJA_ICONO } from './panel/anatomia';

/* post-t7-2c, bloque E (Anexo C.1) -- EL CUADRO DE OPINION. Desde el remate de post-t7-2c (R1, "1. si" de Carlos
   del 15-oct) vive en su pantalla propia, "Gana creditos gratis" (`pages/EarnCreditsPage.js`), y ya no en el Panel.

   ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).

   LAPIDA del remate de post-t7-2c: murieron la "x" que lo escondia 14 dias en el aparato (`sb_opinion_cerrada:<id>`),
   la marca de "opinion pendiente" (`sb_opinion_pendiente:<id>`) que lo pintaba desde el primer render del Panel para
   no empujarlo (la medicion P, "sin salto"), y el reloj de 6 s que retiraba las gracias. Con ellos se retiro su
   suite, `CuadroDeOpinion.sinSalto.pt72c.test.js`: en una pantalla propia no hay nada debajo que empujar.

   LAPIDA de pantallas-alumno (G, firma de opiniones sin limite de la parte 2 y su firma 4): murieron las caras
   "gracias en lugar del cuadro" y "creditos regalados" (GC3 y GC12 dejan de existir; `dashboard:opinion.creditada`
   ya no se pinta). AHORA UNA SOLA CARA, EL CUADRO, SIEMPRE:
     - mientras llega `GET /feedback/estado`: el esqueleto que copia la tarjeta entera (caja, contador y boton);
     - con `enviada: true` (la opinion mas nueva sigue en revision): el renglon verde de las gracias ARRIBA de la
       caja, y la caja sigue para mandar otra;
     - despues de mandar: la caja se vacia, el contador vuelve a "0 / 1,000" y sale el renglon verde, SIN aviso
       flotante;
     - si `/estado` falla: el cuadro sin el renglon y sin aviso (GC13).
   `creditada` ya no decide nada: el servidor nuevo la manda siempre en `false` y uno viejo no la cambia.

   EL BOTON: se ve apagado (al 45 %, `aria-disabled`) con menos de 40 caracteres sin espacios a la orilla, y al
   tocarlo pinta el aviso de hoy. Mientras envia, TRABAJANDO (giro encima, mismo ancho, no se toca). Sin red (el
   detector de la app, P17), tambien apagado, y al tocarlo "You need a connection". El contador "n / 1,000" cuenta
   como el de los motivos de cancelacion (`CancelarConMotivo`), con coma de millar escrita a mano, y al tope se
   pinta en naranja (P18, como el de cancelar).

   LOS ERRORES de la ruta se pintan con el texto que deja la pieza de la casa (`lib/api.js` traduce por
   `error_code` contra `common.errors`); sin respuesta, `common:errors.network`. Un 409 `feedback_already_sent`
   (servidor viejo, ventana de despliegue) deja su aviso y el renglon verde: ya la tenia.

   LA RAIZ DORMIDA NO PIDE NADA (PAGER-1, `lib/pager/activacion`): fuera del carril la raiz siempre esta activa; se
   conserva por si un dia se monta dentro. El estado se pide una sola vez por montaje y por cuenta.

   EL CLIENTE SE PIDE AL LLAMAR: la regla de la casa para lo que el chasis consume es no importar `lib/api` arriba.

   CADUCA (post-t7-2-dinero, bloque H, #656): "al salir de esta pantalla deberia de quitarse ese recuadro verde y no
   volver a salir hasta que vuelva a enviar feedback" (Carlos). El renglon verde sale SOLO despues de un envio con
   exito EN ESTA VISITA (estado local que nace en falso): al salir de la pagina y volver ya no esta. El cuadro ya no
   pide `GET /feedback/estado` (la ruta del servidor se queda: la lee la app 1.3.0 de la tienda), asi que tampoco
   hay esqueleto de espera, ni "raiz dormida", ni el 409 viejo pinta el renglon. Lo de arriba sobre `/estado`, el
   esqueleto y GC13 queda como historia. */

function cliente() {
  return require('../lib/api').default;
}

export const LARGO_MINIMO = 40;
export const LARGO_MAXIMO = 1000;
export const AVISO_ID = 'opinion-corta';
const TARJETA = 'rounded-[18px] border border-[#222222] bg-[#141414] p-4 md:p-5 flex flex-col gap-4';

/** "1,234": el separador de miles a mano, como `conMiles` de `CancelarConMotivo`. */
export function conMiles(n) {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** El boton puede mandar: 40 a 1,000 caracteres sin espacios a la orilla (la regla del servidor). */
export function puedeEnviar(texto) {
  const n = String(texto || '').trim().length;
  return n >= LARGO_MINIMO && n <= LARGO_MAXIMO;
}

/** El renglon verde sale mientras la opinion mas nueva sigue en revision (`enviada: true`). */
export function hayOpinionEnRevision(datos) {
  return Boolean(datos && datos.enviada === true);
}

function Encabezado({ t }) {
  return (
    <div className="flex items-start gap-3 min-w-0">
      <div className={CAJA_ICONO} aria-hidden="true">
        <Gift className="w-5 h-5 text-[#F27A2E]" />
      </div>
      <div className="min-w-0 flex flex-col gap-1">
        <h2 className="text-[18px] leading-snug font-bold text-[#F4F4F3] break-words" data-testid="opinion-titulo">
          {t('dashboard:opinion.titulo')}
        </h2>
        <p className="text-[14.5px] leading-snug text-[#9A9A9A] break-words" data-testid="opinion-texto">
          {t('dashboard:opinion.texto')}
        </p>
      </div>
    </div>
  );
}

export default function CuadroDeOpinion() {
  const { t, ready, i18n } = useReadyTranslation(['dashboard', 'common']);
  const { user } = useAuth();
  const userId = user?.id;
  const esInvitado = Boolean(user?.is_guest);
  const net = useNetworkStatus();

  // post-t7-2-dinero (H): el renglon verde es de ESTA visita; nace en falso y solo un envio con exito lo prende.
  const [enviadaEnEstaVisita, setEnviadaEnEstaVisita] = useState(false);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);

  if (!userId || esInvitado) return null;

  if (!ready) {
    return (
      <div className={TARJETA} data-testid="opinion-cargando" aria-hidden="true">
        <div className="flex items-start gap-3">
          <BloqueEsqueleto className="w-10 h-10 rounded-xl shrink-0" />
          <div className="flex-1 flex flex-col gap-2">
            <BloqueEsqueleto className="h-5 w-3/4 rounded-md" />
            <BloqueEsqueleto className="h-4 w-full rounded-md" />
            <BloqueEsqueleto className="h-4 w-5/6 rounded-md" />
          </div>
        </div>
        <div data-parte="esq-caja"><BloqueEsqueleto className="h-[140px] w-full rounded-[14px]" /></div>
        <div className="flex items-center justify-between gap-3">
          <div data-parte="esq-contador"><BloqueEsqueleto className="h-4 w-20 rounded-md" /></div>
          <div data-parte="esq-boton"><BloqueEsqueleto className="h-11 md:h-10 w-24 rounded-xl" /></div>
        </div>
      </div>
    );
  }

  const listo = puedeEnviar(texto);
  const sinRed = Boolean(net.isOffline);

  const enviar = async () => {
    if (enviando) return;
    setEnviando(true);
    try {
      const lang = String(i18n?.language || '').toLowerCase().startsWith('es') ? 'es' : 'en';
      await cliente().post('/feedback', { text: texto.trim(), lang });
      // GC9: sin aviso flotante; la caja se vacia y sale el renglon verde
      setTexto('');
      setEnviadaEnEstaVisita(true);
    } catch (err) {
      const datos = err?.response?.data;
      const detalle = typeof datos?.detail === 'string' ? datos.detail : null;
      toast.error(detalle || t('common:errors.network'));
    } finally {
      setEnviando(false);
    }
  };

  const alTocarApagado = () => {
    if (sinRed) { avisarSinRed(t); return; }
    toast.error(t('dashboard:opinion.falta'), { id: AVISO_ID });
  };

  return (
    <div className={TARJETA} data-testid="opinion-card">
      <Encabezado t={t} />
      {enviadaEnEstaVisita ? (
        <div role="status" data-testid="opinion-gracias"
          className="flex items-center gap-2.5 rounded-xl border border-[#1F4A2E] bg-[#0D1A12] px-3.5 py-2.5 text-[14px] leading-snug text-[#F4F4F3]">
          <CheckCircle2 className="w-[18px] h-[18px] shrink-0 text-[#4ADE80]" aria-hidden="true" />
          <span className="min-w-0 break-words">{t('dashboard:opinion.gracias')}</span>
        </div>
      ) : null}
      <CajaLargaConContador
        id="opinion-caja"
        alto={140}
        valor={texto}
        onCambio={setTexto}
        maxLength={LARGO_MAXIMO}
        placeholder={t('dashboard:opinion.marcador')}
        aria-label={t('dashboard:opinion.titulo')}
        data-testid="opinion-caja"
      />
      <div className="flex items-center justify-between gap-3 -mt-2">
        <p className={`text-[13px] tabular-nums ${texto.length >= LARGO_MAXIMO ? 'text-[#F7A06A]' : 'text-[#9A9A9A]'}`}
          aria-live="polite" data-testid="opinion-contador">
          {t('dashboard:opinion.contador', { n: conMiles(texto.length) })}
        </p>
        <Boton
          testid="opinion-enviar"
          trabajando={enviando}
          apagadoSuave={!listo}
          sinRed={sinRed}
          alTocarApagado={alTocarApagado}
          onClick={enviar}
          className="shrink-0 min-w-[96px]"
        >
          {t('dashboard:opinion.enviar')}
        </Boton>
      </div>
    </div>
  );
}
